// Production monitoring and analytics utilities

export interface PerformanceMetrics {
  loadTime: number;
  renderTime: number;
  apiResponseTimes: Record<string, number>;
  memoryUsage: number;
  bundleSize: number;
}

export interface ErrorEvent {
  message: string;
  stack?: string;
  url: string;
  lineNumber?: number;
  columnNumber?: number;
  timestamp: number;
  userAgent: string;
  userId?: string;
  sessionId: string;
  buildVersion: string;
}

export interface UserEvent {
  event: string;
  properties: Record<string, any>;
  userId?: string;
  sessionId: string;
  timestamp: number;
  page: string;
}

class MonitoringService {
  private sessionId: string;
  private userId?: string;
  private buildVersion: string;
  private performanceEntries: PerformanceEntry[] = [];
  private apiResponseTimes: Record<string, number> = {};

  constructor() {
    this.sessionId = this.generateSessionId();
    this.buildVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';
    this.initializeMonitoring();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeMonitoring() {
    // Global error handling
    window.addEventListener('error', (event) => {
      this.trackError({
        message: event.message,
        stack: event.error?.stack,
        url: event.filename,
        lineNumber: event.lineno,
        columnNumber: event.colno,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        userId: this.userId,
        sessionId: this.sessionId,
        buildVersion: this.buildVersion,
      });
    });

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError({
        message: `Unhandled Promise Rejection: ${event.reason}`,
        stack: event.reason?.stack,
        url: window.location.href,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        userId: this.userId,
        sessionId: this.sessionId,
        buildVersion: this.buildVersion,
      });
    });

    // Performance monitoring
    if ('PerformanceObserver' in window) {
      this.initializePerformanceMonitoring();
    }

    // Page visibility tracking
    document.addEventListener('visibilitychange', () => {
      this.trackEvent('page_visibility_change', {
        hidden: document.hidden,
        visibilityState: document.visibilityState,
      });
    });

    // Track page load
    window.addEventListener('load', () => {
      this.trackPageLoad();
    });
  }

  private initializePerformanceMonitoring() {
    const observer = new PerformanceObserver((list) => {
      this.performanceEntries.push(...list.getEntries());
      
      // Track navigation timing
      list.getEntries().forEach((entry) => {
        if (entry.entryType === 'navigation') {
          const navEntry = entry as PerformanceNavigationTiming;
          this.trackEvent('page_performance', {
            loadTime: navEntry.loadEventEnd - navEntry.loadEventStart,
            domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart,
            firstPaint: this.getFirstPaint(),
            firstContentfulPaint: this.getFirstContentfulPaint(),
          });
        }
      });
    });

    observer.observe({ entryTypes: ['navigation', 'paint', 'measure'] });
  }

  private getFirstPaint(): number | null {
    const paintEntries = performance.getEntriesByType('paint');
    const firstPaint = paintEntries.find(entry => entry.name === 'first-paint');
    return firstPaint ? firstPaint.startTime : null;
  }

  private getFirstContentfulPaint(): number | null {
    const paintEntries = performance.getEntriesByType('paint');
    const fcp = paintEntries.find(entry => entry.name === 'first-contentful-paint');
    return fcp ? fcp.startTime : null;
  }

  public setUserId(userId: string) {
    this.userId = userId;
  }

  public trackError(error: ErrorEvent) {
    console.error('Application Error:', error);
    
    // In production, send to error tracking service (Sentry, LogRocket, etc.)
    if (process.env.NODE_ENV === 'production') {
      this.sendToErrorService(error);
    }
  }

  public trackEvent(event: string, properties: Record<string, any> = {}) {
    const userEvent: UserEvent = {
      event,
      properties,
      userId: this.userId,
      sessionId: this.sessionId,
      timestamp: Date.now(),
      page: window.location.pathname,
    };

    console.log('User Event:', userEvent);

    // In production, send to analytics service
    if (process.env.NODE_ENV === 'production') {
      this.sendToAnalyticsService(userEvent);
    }
  }

  public trackApiCall(url: string, method: string, startTime: number, endTime: number, status: number) {
    const responseTime = endTime - startTime;
    this.apiResponseTimes[`${method} ${url}`] = responseTime;

    this.trackEvent('api_call', {
      url,
      method,
      responseTime,
      status,
      isError: status >= 400,
    });

    // Track slow API calls
    if (responseTime > 2000) {
      this.trackEvent('slow_api_call', {
        url,
        method,
        responseTime,
        status,
      });
    }
  }

  public trackPageLoad() {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    if (navigation) {
      this.trackEvent('page_load', {
        loadTime: navigation.loadEventEnd - navigation.loadEventStart,
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
        firstPaint: this.getFirstPaint(),
        firstContentfulPaint: this.getFirstContentfulPaint(),
        redirectTime: navigation.redirectEnd - navigation.redirectStart,
        dnsTime: navigation.domainLookupEnd - navigation.domainLookupStart,
        connectTime: navigation.connectEnd - navigation.connectStart,
        responseTime: navigation.responseEnd - navigation.responseStart,
      });
    }
  }

  public getPerformanceMetrics(): PerformanceMetrics {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    return {
      loadTime: navigation ? navigation.loadEventEnd - navigation.loadEventStart : 0,
      renderTime: this.getFirstContentfulPaint() || 0,
      apiResponseTimes: { ...this.apiResponseTimes },
      memoryUsage: (performance as any).memory?.usedJSHeapSize || 0,
      bundleSize: 0, // Would need to be calculated during build
    };
  }

  public trackUserAction(action: string, target?: string, properties: Record<string, any> = {}) {
    this.trackEvent('user_action', {
      action,
      target,
      ...properties,
    });
  }

  public trackFeatureUsage(feature: string, properties: Record<string, any> = {}) {
    this.trackEvent('feature_usage', {
      feature,
      ...properties,
    });
  }

  public trackConversion(event: string, value?: number, properties: Record<string, any> = {}) {
    this.trackEvent('conversion', {
      conversionEvent: event,
      value,
      ...properties,
    });
  }

  private async sendToErrorService(error: ErrorEvent) {
    try {
      // In production, replace with actual error tracking service
      // Example: Sentry, LogRocket, Bugsnag, etc.
      await fetch('/api/errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(error),
      });
    } catch (e) {
      console.error('Failed to send error to tracking service:', e);
    }
  }

  private async sendToAnalyticsService(event: UserEvent) {
    try {
      // In production, replace with actual analytics service
      // Example: Google Analytics, Mixpanel, Amplitude, etc.
      await fetch('/api/analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event),
      });
    } catch (e) {
      console.error('Failed to send event to analytics service:', e);
    }
  }

  // React hook for component performance tracking
  public useComponentPerformance(componentName: string) {
    const startTime = performance.now();
    
    return {
      onMount: () => {
        const mountTime = performance.now() - startTime;
        this.trackEvent('component_mount', {
          component: componentName,
          mountTime,
        });
      },
      onUnmount: () => {
        this.trackEvent('component_unmount', {
          component: componentName,
        });
      },
    };
  }
}

// Singleton instance
export const monitoring = new MonitoringService();

// React hook for easy component integration
export const useMonitoring = (componentName?: string) => {
  const trackEvent = (event: string, properties?: Record<string, any>) => {
    monitoring.trackEvent(event, { component: componentName, ...properties });
  };

  const trackUserAction = (action: string, properties?: Record<string, any>) => {
    monitoring.trackUserAction(action, componentName, properties);
  };

  const trackFeatureUsage = (feature: string, properties?: Record<string, any>) => {
    monitoring.trackFeatureUsage(feature, { component: componentName, ...properties });
  };

  return {
    trackEvent,
    trackUserAction,
    trackFeatureUsage,
    setUserId: monitoring.setUserId.bind(monitoring),
    getPerformanceMetrics: monitoring.getPerformanceMetrics.bind(monitoring),
  };
};

// API monitoring middleware
export const apiMonitoringMiddleware = (url: string, method: string) => {
  const startTime = performance.now();
  
  return {
    onSuccess: (status: number) => {
      const endTime = performance.now();
      monitoring.trackApiCall(url, method, startTime, endTime, status);
    },
    onError: (status: number) => {
      const endTime = performance.now();
      monitoring.trackApiCall(url, method, startTime, endTime, status);
      monitoring.trackError({
        message: `API Error: ${method} ${url}`,
        url: window.location.href,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        userId: undefined,
        sessionId: monitoring['sessionId'],
        buildVersion: monitoring['buildVersion'],
      });
    },
  };
}; 